<div class="dialog-container vd-dialog">
  <!-- Header -->
  <div class="dialog-header vd-header">
    <div class="user-edit-icon">
      <!-- same heart as the Health Dashboard's Vitals card -->
      <svg class="vd-header-icon" viewBox="0 0 24 24" aria-hidden="true">
        <!-- solid black heart -->
        <path style="fill:#0066cc;stroke:none" d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z"/>
        <!-- ECG line: black outline (so the part beyond the heart stays visible on the white circle) -->
        <path style="fill:none;stroke:#0066cc;stroke-width:3.2;stroke-linecap:butt;stroke-linejoin:round" d="M1.2 12.5H7.5L9 10L11 14.5L13.5 7L15.5 15L17 11.5L18 12.5H22.8"/>
        <!-- ECG line: white core -->
        <path style="fill:none;stroke:#fff;stroke-width:1.5;stroke-linecap:butt;stroke-linejoin:round" d="M1.2 12.5H7.5L9 10L11 14.5L13.5 7L15.5 15L17 11.5L18 12.5H22.8"/>
      </svg>
    </div>
    <div class="header-text">
      <h2 class="vd-title">Add Vitals</h2>
      <p class="vd-subtitle">Select a device and enter the reading.</p>
    </div>
    <img class="close-button" (click)="onCancel()" src="assets/images/close-circle.svg" alt="Close" role="button" tabindex="0"
         (keydown.enter)="onCancel()" />
  </div>

  <!-- Body: device cards + the selected device's reading form (one continuous step) -->
  <div class="dialog-content vd-content">
    <h3 class="vd-section-title" id="vd-devices-label">Choose a device</h3>
    <div class="vd-cards" role="group" aria-labelledby="vd-devices-label">
      <button type="button" class="vd-card" *ngFor="let device of devices"
              [class.is-selected]="selected?.systemId === device.systemId"
              [attr.aria-pressed]="selected?.systemId === device.systemId"
              [disabled]="isSaving"
              (click)="select(device)">
        <span class="vd-card-check" aria-hidden="true">✓</span>
        <i [class]="deviceIcon(device.systemId)" class="vd-card-icon" aria-hidden="true"></i>
        <span class="vd-card-label">{{ device.title }}</span>
      </button>
    </div>

    <p class="vd-hint" *ngIf="!selected">Select a device above to enter its reading.</p>

    <form *ngIf="selected && form" [formGroup]="form" (ngSubmit)="onSubmit()" class="vd-form" id="vd-form" novalidate>

      <!-- Single-value devices: Blood Glucose, Blood Oxygen, Weight, Temperature -->
      <div class="vd-field" *ngIf="selected.title !== 'Blood Pressure'">
        <label class="vd-label" for="vd-value1">{{ selected.title }} ({{ selected.valueUnit1 }}) <span class="vd-req" aria-hidden="true">*</span></label>
        <input id="vd-value1" class="vd-input" type="number" placeholder="Enter reading" formControlName="value1"
               maxlength="3" [min]="0" required [class.is-invalid]="fieldError('value1')" [attr.aria-invalid]="fieldError('value1') ? true : null"
               (input)="getChange('value1')" (keypress)="preventNonNumeric($event)" (paste)="preventPasteNonNumeric($event)" />
        <div class="vd-error" role="alert" *ngIf="fieldError('value1') as err">{{ err }}</div>
      </div>

      <!-- Blood Glucose: when did you check? (tags come from the existing tag API) -->
      <div class="vd-field" *ngIf="selected.title === 'Blood Glucose'">
        <span class="vd-label" id="vd-meal-label">When did you check?</span>
        <div class="vd-meals" role="radiogroup" aria-labelledby="vd-meal-label">
          <button type="button" class="vd-meal" role="radio" *ngFor="let meal of mealTypes; let i = index"
                  [id]="'vd-meal-' + i" [class.is-selected]="selectedMeal === meal.key"
                  [attr.aria-checked]="selectedMeal === meal.key" [attr.tabindex]="mealTabIndex(i)"
                  (click)="selectMeal(meal)" (keydown)="onMealKey($event, i)">
            <img [src]="meal.icon" alt="" class="vd-meal-icon" />
            <span class="vd-meal-label">{{ meal.label }}</span>
          </button>
        </div>
      </div>

      <!-- Blood Pressure: systolic / diastolic / pulse -->
      <ng-container *ngIf="selected.title === 'Blood Pressure'">
        <div class="vd-row">
          <div class="vd-field">
            <label class="vd-label" for="vd-value1">Systolic ({{ selected.valueUnit1 }}) <span class="vd-req" aria-hidden="true">*</span></label>
            <input id="vd-value1" class="vd-input" type="number" placeholder="Enter systolic" formControlName="value1" maxlength="3" [min]="0" required
                   [class.is-invalid]="fieldError('value1')" [attr.aria-invalid]="fieldError('value1') ? true : null"
                   (input)="getChange('value1')" (keypress)="preventNonNumeric($event)" (paste)="preventPasteNonNumeric($event)" />
            <div class="vd-error" role="alert" *ngIf="fieldError('value1') as err">{{ err }}</div>
          </div>
          <div class="vd-field">
            <label class="vd-label" for="vd-value2">Diastolic ({{ selected.valueUnit2 }}) <span class="vd-req" aria-hidden="true">*</span></label>
            <input id="vd-value2" class="vd-input" type="number" placeholder="Enter diastolic" formControlName="value2" maxlength="3" [min]="0" required
                   [class.is-invalid]="fieldError('value2')" [attr.aria-invalid]="fieldError('value2') ? true : null"
                   (input)="getChange('value2')" (keypress)="preventNonNumeric($event)" (paste)="preventPasteNonNumeric($event)" />
            <div class="vd-error" role="alert" *ngIf="fieldError('value2') as err">{{ err }}</div>
          </div>
        </div>
        <div class="vd-field">
          <label class="vd-label" for="vd-value3">Pulse ({{ selected.valueUnit3 }}) <span class="vd-req" aria-hidden="true">*</span></label>
          <input id="vd-value3" class="vd-input" type="number" placeholder="Enter pulse" formControlName="value3" maxlength="3" [min]="0" required
                 [class.is-invalid]="fieldError('value3')" [attr.aria-invalid]="fieldError('value3') ? true : null"
                 (input)="getChange('value3')" (keypress)="preventNonNumeric($event)" (paste)="preventPasteNonNumeric($event)" />
          <div class="vd-error" role="alert" *ngIf="fieldError('value3') as err">{{ err }}</div>
        </div>
      </ng-container>

      <!-- Date / Time -->
      <div class="vd-row">
        <div class="vd-field">
          <label class="vd-label" for="vd-date">Date <span class="vd-req" aria-hidden="true">*</span></label>
          <div class="vd-input-wrap">
            <input id="vd-date" class="vd-input vd-input--date" [matDatepicker]="picker" [max]="maxDate" formControlName="readingDate" readonly
                   placeholder="Select date" (focus)="picker.open()" (click)="picker.open()" />
            <mat-datepicker-toggle class="vd-date-toggle" [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </div>
        </div>
        <div class="vd-field">
          <label class="vd-label" for="vd-time">Time <span class="vd-req" aria-hidden="true">*</span></label>
          <div class="vd-input-wrap">
            <img src="assets/images/health/icons/Frame 1000004270.svg" class="vd-time-icon" alt="" />
            <input id="vd-time" class="vd-input vd-input--time" type="time" formControlName="readingTime" />
          </div>
        </div>
      </div>

      <!-- Add More Details (Optional) (Optional) -->
      <div class="vd-field vd-field--last">
        <label class="vd-label" for="vd-comment">Add More Details (Optional)</label>
        <textarea id="vd-comment" class="vd-input vd-textarea" rows="2" formControlName="comment" placeholder="Type here..."></textarea>
      </div>
    </form>
  </div>

  <!-- Footer -->
  <div class="vd-actions">
    <button type="button" class="vd-btn vd-btn--secondary" (click)="onCancel()" [disabled]="isSaving">Cancel</button>
    <button type="submit" form="vd-form" class="vd-btn vd-btn--primary" [disabled]="!selected || isSaving">
      <!-- <svg class="vd-btn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg> -->
      <span>{{ isSaving ? 'Saving…' : 'Save Reading' }}</span>
    </button>
  </div>
</div>